import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { marked } from 'marked';
import { omit } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { CANVAS_CREATIVE_ID, CANVAS_ICONS } from 'site-modules/canvas/constants/canvas';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';

import './action-buttons.scss';

const THUMB = { UP: 'up', DOWN: 'down' };

export function ActionButtons({ messageText, isCanceled = false, messageId = null }) {
  const [selectedThumb, setSelectedThumb] = useState(null);
  const [animationKeys, setAnimationKeys] = useState({
    copy: 0,
    [THUMB.UP]: 0,
    [THUMB.DOWN]: 0,
  });

  const copyToClipboard = useCallback(
    async messagePlain => {
      setAnimationKeys(prevAnimationKeys => ({ ...prevAnimationKeys, copy: prevAnimationKeys.copy + 1 }));

      const rawHtml = marked.parse(messagePlain);
      const htmlBlob = new Blob([`<html lang="en"><body>${rawHtml}</body></html>`], { type: 'text/html' });
      const textBlob = new Blob([messagePlain], { type: 'text/plain' });

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': htmlBlob, // Slack, Google Docs, Word pick this up
          'text/plain': textBlob, // fallback for plain-text targets (IDEs, terminals)
        }),
      ]);

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
        event_data: {
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          action_name: TrackingConstant.ACTION_TOOL_SELECT,
          subaction_name: 'copy_message',
          creative_id: CANVAS_CREATIVE_ID,
          resp_uuid: messageId,
        },
      });
    },
    [messageId]
  );

  const handleCopy = useCallback(() => {
    copyToClipboard(messageText);
  }, [copyToClipboard, messageText]);

  const onThumbClick = useCallback(
    subAction => event => {
      const thumb = event.currentTarget.dataset.thumb;
      setAnimationKeys(prevAnimationKeys => ({ ...prevAnimationKeys, [thumb]: prevAnimationKeys[thumb] + 1 }));
      setSelectedThumb(thumb);

      EventToolbox.fireTrackAction({
        event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
        event_data: {
          action_category: TrackingConstant.USER_ACTION_CATEGORY,
          action_cause: TrackingConstant.ACTION_CAUSE_BUTTON_CLICK,
          action_name: TrackingConstant.ENGAGE_SURVEY,
          subaction_name: subAction,
          creative_id: CANVAS_CREATIVE_ID,
          resp_uuid: messageId,
        },
      });
    },
    [messageId]
  );

  const actions = [
    {
      icon: CANVAS_ICONS.COPY,
      label: 'Copy to clipboard',
      onClick: handleCopy,
      iconKey: animationKeys.copy,
      iconClassName: classnames({ 'icon-fade': !!animationKeys.copy }),
      overlay: (
        <i
          key={`copy-${animationKeys.copy}`}
          className={classnames('icon-appear icon-done size-20 pos-a top-0 left-0 right-0 bottom-0', {
            animating: !!animationKeys.copy,
          })}
          aria-hidden
        />
      ),
    },
    {
      icon: selectedThumb === THUMB.UP ? CANVAS_ICONS.THUMB_UP_SOLID : CANVAS_ICONS.THUMB_UP,
      iconKey: animationKeys[THUMB.UP],
      iconClassName: classnames({
        'thumb-animating': !!animationKeys[THUMB.UP],
      }),
      label: 'Helpful answer',
      onClick: onThumbClick('approval_submit'),
      thumb: THUMB.UP,
    },
    {
      icon: selectedThumb === THUMB.DOWN ? CANVAS_ICONS.THUMB_DOWN_SOLID : CANVAS_ICONS.THUMB_DOWN,
      iconKey: animationKeys[THUMB.DOWN],
      iconClassName: classnames({
        'thumb-animating': !!animationKeys[THUMB.DOWN],
      }),
      label: 'Not helpful answer',
      onClick: onThumbClick('disapproval_submit'),
      thumb: THUMB.DOWN,
    },
  ];

  return (
    <ul
      className={classnames('action-buttons fade show list-unstyled mb-0_25 gap-0_75', {
        'd-flex': !isCanceled,
        'd-none': isCanceled,
      })}
    >
      {actions.map(({ icon, iconKey, iconClassName, label, onClick, thumb, overlay }) => (
        <li key={label}>
          <PopoverItem
            screenReaderTooltipLabel={label}
            innerClassName="text-nowrap px-0_75 py-0_5 text-cool-gray-30"
            boundariesElement="clippingParents"
            trigger="hover focus"
            delay={{ show: 0, hide: 0 }}
            fade={false}
            renderTarget={(targetProps, tooltipRef) => (
              <Button
                {...omit(targetProps, ['onKeyDown'])}
                aria-label={label}
                innerRef={tooltipRef}
                className="p-0 rounded pos-r"
                onClick={onClick}
                color="link"
                data-thumb={thumb}
              >
                <img key={iconKey} className={classnames('icon', iconClassName)} src={icon} alt="" />
                {overlay}
              </Button>
            )}
          >
            {label}
          </PopoverItem>
        </li>
      ))}
    </ul>
  );
}

ActionButtons.propTypes = {
  messageText: PropTypes.string.isRequired,
  isCanceled: PropTypes.bool,
  messageId: PropTypes.string,
};
